import store from '~/store';
import type { IUserInfo, ILoginParams, IRegistParams } from '~/services/UserServer/types';
import type { IRoleRights, Review, ReviewsJson, ReviewJsonItem } from '~/services/BasicServer/types';
import { Roles } from '~/services/BasicServer/types';
import UserServer from '~/services/UserServer';
import BasicServer from '~/services/BasicServer';
import { NetCode, Token } from '~/types/const';
import useBasicStore from './basic';
import { convertJsonToReview } from '~/config/reviews';

const useUserStore = defineStore('user', ()=>{
    const token = ref("")
    const reviews = ref<Review[]>([]) // 评价列表
    async function login(params: ILoginParams, callback: ()=>void, errorCallback: (message: string)=>void){
        let res = await UserServer.login<string>(params)
        if(res.code === NetCode.SUCCESS){
            token.value = res.data as string
            setTimeout(()=> getUserInfo())
            setTimeout(()=> callback())
        }else{
            errorCallback(res.message)
        }
    }

    async function getAccountRegist(params: IRegistParams, callback: ()=>void, errorCallback: (message: string)=>void){
        let res = await UserServer.getAccountRegist(params)
        if(res.code === NetCode.SUCCESS){
            token.value = res.data as string
            setTimeout(()=> getUserInfo())
            setTimeout(()=> callback())
        }else{
            errorCallback(res.message)
        }
    }
    function tokenFail(){
        token.value = ""
        useCookie(Token).value = ""
        userInfo.value = {}
        localStorage.setItem("user", JSON.stringify({}))
        localStorage.removeItem("user_token")
    }

    const userInfo = ref<IUserInfo | Object>({})
    async function getLocalUserInfo(){
        let user = localStorage.getItem("user")
        if(user){
            let userData = JSON.parse(user)
            if(userData.userInfo){
                userInfo.value = userData.userInfo
            }
            if(userData.roleRightsMap){
                roleRightsMap.value = userData.roleRightsMap
            }
            if(userData.token){
                token.value = userData.token
            }
        }
    }
    async function getUserInfo(){
        let res = await UserServer.getUserInfo<IUserInfo>()
        if(res.code === NetCode.SUCCESS){
            userInfo.value = res.data as IUserInfo
        }
    }

    const roleRightsMap = ref<Record<Roles, IRoleRights> | null>(null)
    async function getRoleRights(){
        let res = await BasicServer.getRoleRights()
        if(res.code === NetCode.SUCCESS){
            roleRightsMap.value = res.data as Record<Roles, IRoleRights>
        }
    }

    async function logout(callback: ()=>void){
        tokenFail()
        callback()
    }
    // 监听 homePageConfig 的变化
    // watch(() => useBasicStore().homePageConfig, (newVal) => {
    //     console.log("userRights.value", userRights.value)
    // }, { deep: true })

    const userRights = computed(()=>{
        // 用户角色 roleRightsMap
        const basicStore = useBasicStore()
        const { homePageConfig } = storeToRefs(basicStore)
        // 定义角色 普通用户加油包的时候 获取vip的相关权益
        // const userRole = formatUseBiz(userInfo.value)
        let map = {
            ...userInfo.value,
            // ...roleRightsMap.value?.[userRole],
            ...homePageConfig.value,
        }
        // map.batchUploadLimit = map.batchUploadLimit || 1
        return map
    })

    const userRemark = computed(() => {
        if (userInfo.value?.remark) {
            return JSON.parse(userInfo.value?.remark)
        }
        return null
    })

    // 检查用户是否能上传
    function getCheckUserUpload(){
        return true
    }

    // 根据当前语言获取币种
    function getCurrentCoin(lang:string) {
        const basicStore = useBasicStore()
        const { isChina, localtionData } = storeToRefs(basicStore)
        if (isChina.value) {
            return "cny"
        }
        let configKey = ""
        if (lang == "zh") {
            configKey = "cny"
        }else if (lang == "en") {
            if (localtionData.value.toLowerCase() == "gb") {
                configKey = "gbp"
            }else {
                configKey = "usd"
            }
        }else if (lang == "ja") {
            configKey = "jpy"
        }else if (lang == "zh-TW") {
            if (localtionData.value.toLowerCase() == "hk") {
                configKey = "hkd"
            }else {
                configKey = "twd"
            }
        }else if (lang == "ko") {
            configKey = "krw"
        }else if (lang == "hi") {
            configKey = "usd"
        }else if (lang == "ar") {
            configKey = "usd"
        }else if (lang == "de") {
            configKey = "eur"
        }else if (lang == "fr") {
            configKey = "eur"
        }else if (lang == "id") {
            configKey = "usd"
        }else if (lang == "itb") {
            configKey = "eur"
        }else if (lang == "uk") {
            configKey = "usd"
        }else {
            configKey = "usd"
        }
        return configKey
    }

    // 根据当前语言获取币种符号
    function getCurrentLangSymbol(lang:string) {
        const basicStore = useBasicStore()
        const { isChina, localtionData } = storeToRefs(basicStore)
        let coinKey = getCurrentCoin(lang)
        console.log("getCurrentLangSymbol===", coinKey, lang)
        if (isChina.value) {
            return "￥"
        }
        let coin = "￥"
        if (coinKey == "cny") {
          coin = "￥"
        }else if (coinKey == "usd") {
          coin = "$"
        }else if (coinKey == "jpy") {
          coin = "円"
        }else if (coinKey == "eur") {
          coin = "€"
        }else if (coinKey == "krw") {
          coin = "₩"
        }else if (coinKey == "twd") {
          coin = "NT$"
        }else if (coinKey == "hkd") {
          coin = "HK$"
        }else if (coinKey == "gbp") {
          coin = "£"
        }
        return coin
    }

    // 根据当前币种获取币种符号
    function getCurrentCoinSymbol(coin:string) {
        let coinSymbol = "￥"
        let coinKey = coin.toLocaleLowerCase()
        if (coinKey == "cny") {
          coinSymbol = "￥"
        }else if (coinKey == "usd") {
          coinSymbol = "$"
        }else if (coinKey == "jpy") {
          coinSymbol = "円"
        }else if (coinKey == "eur") {
          coinSymbol = "€"
        }else if (coinKey == "krw") {
          coinSymbol = "₩"
        }else if (coinKey == "twd") {
          coinSymbol = "NT$"
        }else if (coinKey == "hkd") {
          coinSymbol = "HK$"
        }else if (coinKey == "gbp") {
          coinSymbol = "£"
        }
        console.log("getCurrentCoinSymbol==", coin, coinKey)
        return coinSymbol
    }

    // 获取列表
    async function getReviews(lang:string){
        try {
            const response = await $fetch<ReviewsJson>('/reviews.json')
            // 语言映射
            const languageMap: Record<string, keyof ReviewsJson> = {
                'zh-CN': '中文',
                'zh': '中文',
                'en': '英文',
                'ja': '日语',
                'de': '德语'
            }
            // 根据当前语言选择对应的评论数据
            const currentLang = languageMap[lang] || languageMap['zh-CN']
            const reviewsData = response[currentLang] || []
            // 转换为 Review 格式
            reviews.value = reviewsData.map((item, index) => convertJsonToReview(item, index, currentLang))
            console.log("reviews.value===", reviews.value)
        } catch (error) {
            console.error('Failed to load reviews:', error)
            // 如果加载失败，使用空数组
            reviews.value = []
        }
    }
    

    return {
        token, tokenFail, login,
        userInfo, getUserInfo,
        roleRightsMap, getRoleRights,
        userRights, userRemark, getCheckUserUpload, getCurrentCoinSymbol,
        getCurrentCoin, logout, getAccountRegist, getLocalUserInfo, getCurrentLangSymbol, reviews, getReviews
    }
}, {
    persist:{
        pick: ['userInfo', 'token', 'roleRightsMap'],
        storage: piniaPluginPersistedstate.localStorage(),
    }
})
/** 在 setup 外使用 */
export function useUserStoreHook() {
    return useUserStore(store)
}

export default useUserStore

// function formatUseRole(userInfo: IUserInfo | null) : Roles {
//     if(!userInfo) return Roles.visitor
//     let remark = userInfo.remark && JSON.parse(userInfo.remark)
//     if(userInfo.vipLevel === 1) {
//         if(remark?.curVipFunPowerId && remark.curVipFunPowerId === 'vip7day') return Roles.vip7day
//         if(remark?.curVipFunPowerId && remark.curVipFunPowerId === 'vipWeek') return Roles.vipWeek
//         return Roles.svip
//     }
//     if(userInfo.vipLevel === 2) {
//         if(remark?.curVipFunPowerId && remark.curVipFunPowerId === 'svipWeek') return Roles.svipWeek
//         return Roles.svip
//     }
//     if(userInfo.vipLevel === 0){
//         if(userInfo.validWordsForBag) return Roles.vip
//         return Roles.user
//     }
//     return Roles.user
// }

function formatUseBiz(enterpriseInfo: EnterpriseInfo | null) : Roles {
    if(!enterpriseInfo) return Roles.base
    if(enterpriseInfo?.serviceLevel === 1) {
        return Roles.base
    }
    if(enterpriseInfo?.serviceLevel === 2) {
        return Roles.middle
    }
    if(enterpriseInfo?.serviceLevel === 3){
        return Roles.high
    }
    return Roles.base
}